import React, { useState, useEffect, useRef } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { ChevronDown, Check } from 'lucide-react';

const LANGUAGES = [
  { code: 'no', countryCode: 'NO', label: 'Norsk', short: 'NO' },
  { code: 'en', countryCode: 'GB', label: 'English', short: 'EN' },
];

export default function LanguageDropdown({ lang, setLang }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEscape = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, []);

  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-2 pl-2.5 pr-3 py-2 rounded-full border border-line bg-surface/80 backdrop-blur-sm text-sm font-semibold text-frost hover:border-volt-400/60 transition-all duration-200"
      >
        <ReactCountryFlag
          countryCode={current.countryCode}
          svg
          style={{ width: '1.25em', height: '1.25em', borderRadius: '3px' }}
          title={current.label}
        />
        <span>{current.short}</span>
        <ChevronDown
          size={14}
          className={`text-mist transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 mt-2 w-44 rounded-2xl border border-line bg-raised shadow-card overflow-hidden z-50"
        >
          {LANGUAGES.map((l) => {
            const active = l.code === lang;
            return (
              <button
                key={l.code}
                role="option"
                aria-selected={active}
                onClick={() => {
                  setLang(l.code);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors duration-150 ${
                  active
                    ? 'bg-volt-400/10 text-volt-400'
                    : 'text-frost hover:bg-white/5'
                }`}
              >
                <ReactCountryFlag
                  countryCode={l.countryCode}
                  svg
                  style={{ width: '1.4em', height: '1.4em', borderRadius: '3px' }}
                  title={l.label}
                />
                <span className="flex-1 text-left">{l.label}</span>
                {active && <Check size={15} className="text-volt-400" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
